import { AuthService } from '../auth/AuthService';
import { ApiBack } from '../axios-config';

interface UploadPart {
	ETag: string;
	PartNumber: number;
}

interface DataEtagInterface {
	upload_id: string;
	nome_arquivo: string;
	parts: UploadPart[];
}

export interface FileInterface {
	id_barragem: string,
	file?: File | null,
	descricao: string,
	tipo: string,
	npgb?: string,
	data_emissao: string | null,
	url?: string,
	nome_arquivo?: string
}

export interface MetaDadosProps {
	data_emissao: string,
	data_registro: string,
	descricao: string,
	finalizado: boolean,
	id_barragem: string,
	nome_arquivo: string,
	npgb: string,
	tamanho: number,
	tipo: string,
	uuid: string,
	url?: string
}


/**
 * Envia um chunk para a URL de upload (S3 presigned URL ou equivalente).
 */
const SendFile = async (uploadUrl: string, file: Blob): Promise<Response> => {
	await AuthService.verify();

	const response = await fetch(uploadUrl, {
		method: 'PUT',
		body: file,
		headers: { 'Content-Type': file.type || 'application/octet-stream' }
	});

	if (!response.ok) {
		throw new Error(`Falha no upload: ${response.status} ${response.statusText}`);
	}

	return response;
};

/**
 * Finaliza o upload multipart enviando os ETags das partes.
 */
const CompletUpload = async (data: DataEtagInterface) => {
	await AuthService.verify();
	const token = await AuthService.getAccessToken();

	if (token instanceof Error) {
		throw new Error(token.message || 'Erro ao obter token');
	}

	try {
		const response = await ApiBack.post('./complete-upload', data, {
			headers: { Authorization: token }
		});
		if (response.data?.status === 'false') {
			throw new Error(response.data?.message || 'Falha ao completar upload');
		}
		return response.data;
	} catch (error) {
		console.error('Erro no complete-upload:', error);
		throw new Error((error as { message?: string }).message || 'Erro ao completar upload');
	}
};

/**
 * Fatia um arquivo em partes de tamanho definido (MB).
 */
function SliceFile(file: File, sliceSizeMB: number) {

	const chunkSize = sliceSizeMB * 1024 * 1024;
	const chunks: { blob: Blob; partNumber: number }[] = [];

	for (let start = 0, part = 1; start < file.size; start += chunkSize, part++) {
		const end = Math.min(start + chunkSize, file.size);
		chunks.push({ blob: file.slice(start, end), partNumber: part });
	}

	return chunks;
}

/**
 * Faz o upload das partes usando as URLs retornadas pelo backend.
 */
async function uploadParts(
	chunks: { blob: Blob; partNumber: number }[],
	urls: { parts: { url: string; partNumber: number }[] },
	name: string,
	upload_id: string
): Promise<DataEtagInterface> {
	if (chunks.length !== urls.parts.length) {
		throw new Error('Número de chunks não corresponde ao número de URLs');
	}

	const parts = await Promise.all(
		chunks.map(async ({ blob, partNumber }, i) => {
			const response = await SendFile(urls.parts[i].url, blob);
			const ETag = response.headers.get('ETag');
			if (!ETag) throw new Error(`ETag não encontrada na parte ${partNumber}`);

			return {
				ETag: ETag.replace(/"/g, ''),
				PartNumber: urls.parts[i].partNumber || partNumber
			};
		})
	);

	return { upload_id, nome_arquivo: name, parts };
}

/**
 * Cria links de upload multipart no backend, envia as partes e finaliza o upload.
 */
async function UploadFile(
	dataObj: FileInterface
) {

	if (!dataObj.file) {
		throw new Error('Nenhum arquivo enviado.');
	}

	if (dataObj.file === null) return new Error('Arquivo invalido ou não existente.');
	const partes = SliceFile(dataObj.file, 100); // 25MB por parte

	await AuthService.verify();
	const token = await AuthService.getAccessToken();

	if (token instanceof Error) {
		throw new Error(token.message || 'Erro ao obter token');
	}

	try {
		const { data: uploadData } = await ApiBack.post('./upload/multipart-urls', {
			nome_arquivo: dataObj.file.name,
			tamanho: dataObj.file.size,
			id_barragem: dataObj.id_barragem,
			tipo: dataObj.tipo,
			npgb: dataObj.npgb ?? '',
			data_emissao: dataObj.data_emissao,
			descricao: dataObj.descricao,
			partes: partes.length
		}, {
			headers: { Authorization: token }
		});

		const responseUpload = await uploadParts(
			partes,
			uploadData.urls,
			uploadData.urls.object_name,
			uploadData.urls.upload_id
		);

		return await CompletUpload(responseUpload);
	} catch (error) {
		console.error('Erro no processo de upload:', error);
		return new Error((error as { message?: string }).message || 'Erro no upload multipart');
	}
}

async function list(id_barragem: string): Promise<MetaDadosProps[] | Error> {
	await AuthService.verify();

	try {
		const token = await AuthService.getAccessToken();

		const response = await ApiBack.get(`/arquivos?uuid=${id_barragem}`, {
			headers: {
				Authorization: `${token}`
			}
		});

		if (response.status !== 200) return Error(`Erro ao listar arquivos. ${response.statusText}`);

		return response.data;
	} catch (error) {
		throw new Error(`Erro ao listar arquivos. ${error}`);
	}
}

async function getMetadados(id_file: string, view?: boolean): Promise<MetaDadosProps | Error> {
	await AuthService.verify();

	try {
		const token = await AuthService.getAccessToken();

		const response = await ApiBack.get(`/arquivo?uuid=${id_file}&view=${view}`, {
			headers: {
				Authorization: `${token}`
			}
		});

		if (response.status !== 200) return Error(`Erro ao buscar arquivo. ${response.statusText}`);

		return response.data;
	} catch (error) {
		throw new Error(`Erro ao buscar arquivo. ${error}`);
	}
}

/**
 * Baixa um arquivo via link do minIO.
 */
const downloadFile = async (downloadUrl: string, fileName?: string) => {
	try {
		const response = await fetch(downloadUrl);
		if (!response.ok) {
			throw new Error(`Falha no download: ${response.status} ${response.statusText}`);
		}


		const blob = await response.blob();

		const objectUrl = window.URL.createObjectURL(blob);

		const urlParts = downloadUrl.split('/');
		let filename = '';
		if(fileName) filename = fileName ||  urlParts[urlParts.length - 1] || 'arquivo';
		// filename = urlParts[urlParts.length - 1] || 'arquivo';
		filename = filename.split('?')[0];
		if (filename.includes('%')) filename.replace('%', ' ');
		console.log('Nome do arquivo:', filename);

		const link = document.createElement('a');
		link.href = objectUrl;
		link.download = filename;
		document.body.appendChild(link);
		link.click();

		document.body.removeChild(link);
		window.URL.revokeObjectURL(objectUrl);

	} catch (error) {
		console.error('Erro detalhado no download:', error);
		throw new Error('Falha ao baixar o arquivo');
	}
};

async function update(id_file: string, data: FileInterface): Promise<MetaDadosProps | Error> {
	await AuthService.verify();

	try {
		const token = await AuthService.getAccessToken();

		const response = await ApiBack.put(`/arquivo?uuid=${id_file}`, data, {
			headers: {
				Authorization: `${token}`
			}
		});

		if (response.status !== 200) return Error(`Erro ao buscar arquivo. ${response.statusText}`);

		return response.data;
	} catch (error) {
		throw new Error(`Erro ao buscar arquivo. ${error}`);
	}
}

async function delet(id_file: string): Promise<string | Error> {
	await AuthService.verify();

	try {
		const token = await AuthService.getAccessToken();

		const response = await ApiBack.delete(`/delete-file?uuid=${id_file}`, {
			headers: {
				Authorization: `${token}`
			}
		});

		if (response.status !== 200) return Error(`Erro ao deletar arquivo. ${response.statusText}`);

		return 'Arquivo excluido';
	} catch (error) {
		throw new Error(`Erro ao deletar arquivo. ${error}`);
	}
}

// async function getDoc(url: string): Promise<string> {

// }

export const MinioService = {
	SendFile,
	UploadFile,
	list,
	getMetadados,
	downloadFile,
	update,
	delet
};